import type * as CC from 'vanilla-cookieconsent';
import { escapeHtml, safeUrl } from '../html';
import type { CookieRows } from '../cookie-table';
import type { CategoryName, OptionalCategory } from '../types';
import enJson from './en.json';
import { isBuiltInLanguage } from './languages';
import { importPack } from './load';
import type { LanguageTexts } from './types';

export type { LanguageTexts, CategoryTexts } from './types';
export { BUILT_IN_LANGUAGES, isBuiltInLanguage } from './languages';

/** English is always bundled: the fallback for any language without a pack or whose pack fails to load. */
export const EN_TEXTS = enJson as LanguageTexts;

const loaded = new Map<string, LanguageTexts>([['en', EN_TEXTS]]);
const pending = new Map<string, Promise<LanguageTexts>>();

/** Texts of a language loaded with `loadLanguage`; English for anything else. */
export function textsFor(code: string): LanguageTexts {
  return loaded.get(code) ?? EN_TEXTS;
}

/** Load a built-in language pack once. Never rejects: on failure it warns and resolves to English. */
export function loadLanguage(code: string): Promise<LanguageTexts> {
  const ready = loaded.get(code);
  if (ready) return Promise.resolve(ready);
  if (!isBuiltInLanguage(code) || code === 'en') return Promise.resolve(EN_TEXTS);
  let promise = pending.get(code);
  if (!promise) {
    promise = importPack(code).then(
      (texts) => {
        loaded.set(code, texts);
        return texts;
      },
      (error: unknown) => {
        console.warn(`[Kockica] Could not load the "${code}" language pack; showing English.`, error);
        pending.delete(code);
        return EN_TEXTS;
      },
    );
    pending.set(code, promise);
  }
  return promise;
}

export interface BuildContext {
  controllerName: string;
  policyUrl: string;
  categories: OptionalCategory[];
  branding: boolean;
  /** Cookie rows per category; adds a cookie table to each section of the settings layer. */
  cookieRows?: CookieRows;
}

/** "a", "a in b", "a, b in c" */
export function joinPurposes(items: string[], and: string): string {
  if (items.length <= 1) return items.join('');
  return `${items.slice(0, -1).join(', ')} ${and} ${items[items.length - 1]}`;
}

/** Credit link. Switch to https://kockica.datavallis.com/ once that subdomain is live (HANDOFF owner action). */
export const CREDIT_URL = 'https://datavallis.com/?utm_source=kockica&utm_medium=banner';

export function brandingHtml(texts: LanguageTexts): string {
  return `<span class="kockica-credit">${escapeHtml(texts.poweredBy)} <a href="${CREDIT_URL}" target="_blank" rel="nofollow noopener">Kockica</a> · Data Vallis</span>`;
}

/** Turn plain language texts into a vanilla-cookieconsent translation. */
export function buildTranslation(texts: LanguageTexts, ctx: BuildContext): CC.Translation {
  const controller = escapeHtml(ctx.controllerName);
  const policyHref = safeUrl(ctx.policyUrl);
  const policyLink = `<a href="${policyHref}">${escapeHtml(texts.policyLinkText)}</a>`;
  const controllerLine = escapeHtml(texts.controllerLine).replace('{controller}', controller);

  const purposes = joinPurposes(
    ctx.categories.map((c) => texts.categories[c].purpose),
    texts.and,
  );
  const lead =
    ctx.categories.length > 0
      ? escapeHtml(texts.consentDescription).replace('{purposes}', escapeHtml(purposes))
      : escapeHtml(texts.consentDescriptionNecessaryOnly);

  const footerParts = [`<a href="${policyHref}">${escapeHtml(texts.policyTitle)}</a>`];
  if (ctx.branding) footerParts.push(brandingHtml(texts));

  const table = (category: CategoryName): { cookieTable: CC.CookieTable } | Record<string, never> => {
    const rows = ctx.cookieRows?.[category];
    if (!rows?.length) return {};
    return {
      cookieTable: {
        headers: { name: texts.tableName, provider: texts.tableProvider, purpose: texts.tablePurpose, duration: texts.tableDuration },
        body: rows.map((r) => ({
          name: escapeHtml(r.name),
          provider: escapeHtml(r.provider),
          purpose: escapeHtml(r.purpose),
          duration: escapeHtml(r.duration),
        })),
      },
    };
  };

  const sections: CC.Section[] = [
    { title: texts.introTitle, description: escapeHtml(texts.introDescription) },
    {
      title: texts.necessaryTitle,
      description: escapeHtml(texts.necessaryDescription),
      linkedCategory: 'necessary',
      ...table('necessary'),
    },
    ...ctx.categories.map((c) => ({
      title: texts.categories[c].title,
      description: escapeHtml(texts.categories[c].description),
      linkedCategory: c,
      ...table(c),
    })),
    {
      title: texts.moreTitle,
      description: `${escapeHtml(texts.moreDescription).replace('{policyLink}', policyLink)} ${controllerLine}`,
    },
  ];

  return {
    consentModal: {
      title: texts.consentTitle,
      description: `${lead} ${controllerLine}`,
      acceptAllBtn: texts.acceptAll,
      acceptNecessaryBtn: texts.rejectAll,
      showPreferencesBtn: texts.preferences,
      footer: footerParts.join(''),
    },
    preferencesModal: {
      title: texts.preferencesTitle,
      acceptAllBtn: texts.acceptAll,
      acceptNecessaryBtn: texts.rejectAll,
      savePreferencesBtn: texts.save,
      closeIconLabel: texts.close,
      serviceCounterLabel: texts.serviceCounter,
      sections,
    },
  };
}
